//@ts-check

import { Timer } from "../Timer";
import { SlideshowKeyboard } from "./SlideshowKeyboard";
import debug from "debug";

const log = debug("slideshow:SlideshowConductor");

/**
 * @typedef {"next" | "prev"} Direction
 *
 * The part of Slideshow the conductor relies on.
 * @typedef {object} SlideshowI
 * @property {HTMLElement} element
 * @property {boolean} navigationEnabled
 * @property {(direction: Direction, options: { manual: boolean }) => void} step
 */

/**
 * Feature flags:
 * - scope: which slideshows advance — only the (partially) visible ones, or all.
 * - keyboard: whether left/right arrow keys navigate.
 */
export const POLICY = {
  /** @type {"visible" | "all"} */
  scope: "visible",
  keyboard: true,
};

const DEFAULT_INTERVAL = 4000;

/**
 * Kepps slideshows on the page in sync.
 * Slideshows have no timer of their own; they only move when told.
 * Slideshows added later (Turbo, scrolled into view) move on the next tick.
 */
class SlideshowConductor {
  constructor() {
    /** @type {Set<SlideshowI>} */
    this.slideshows = new Set();
    /** @type {Set<Element>} */
    this.visibleElements = new Set();
    // Pausing is by reason, so independent sources (arrow hover, ...) combine.
    /** @type {Set<string>} */
    this.pauseReasons = new Set();
    /** @type {Timer | null} */
    this.timer = null;
    this.keyboard = new SlideshowKeyboard(this);

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.visibleElements.add(entry.target);
        } else {
          this.visibleElements.delete(entry.target);
        }
      });
    });
  }

  /**
   * Called when a Slideshow is initialized.
   * The element is added to the intersection observer.
   * @param {SlideshowI} slideshow
   */
  register(slideshow) {
    log("register", slideshow.element);
    this.slideshows.add(slideshow);
    this.observer.observe(slideshow.element);

    if (!this.timer) this._start(slideshow);
  }

  /** @param {SlideshowI} slideshow */
  unregister(slideshow) {
    log("unregister", slideshow.element);
    this.slideshows.delete(slideshow);
    this.observer.unobserve(slideshow.element);
    this.visibleElements.delete(slideshow.element);

    if (this.slideshows.size === 0) this._stop();
  }

  // Manual steps (arrows, keyboard) restart the clock, so the next automatic
  // step is a full interval away.
  next() {
    this._stepManually("next");
  }

  prev() {
    this._stepManually("prev");
  }

  /** @param {string} reason */
  pause(reason) {
    this.pauseReasons.add(reason);
    this.timer?.pause();
  }

  /** @param {string} reason */
  resume(reason) {
    this.pauseReasons.delete(reason);
    if (this.pauseReasons.size === 0) this.timer?.resume();
  }

  /** @param {Direction} direction */
  _stepManually(direction) {
    this._targets()
      .filter((slideshow) => slideshow.navigationEnabled)
      .forEach((slideshow) => slideshow.step(direction, { manual: true }));
    this.timer?.restart();
  }

  _tick() {
    this._targets().forEach((slideshow) =>
      slideshow.step("next", { manual: false }),
    );
  }

  /** @returns {SlideshowI[]} */
  _targets() {
    const all = Array.from(this.slideshows);
    if (POLICY.scope === "all") return all;
    return all.filter((slideshow) =>
      this.visibleElements.has(slideshow.element),
    );
  }

  /**
   * Starts the timer and attaches the keyboard listener.
   * Called whenever the first slideshow is registered.
   *
   * @param {SlideshowI} slideshow
   */
  _start(slideshow) {
    // The interval is a site-wide setting, it will be the same on all slideshow elements.
    const interval =
      parseInt(slideshow.element.dataset.slideshowInterval ?? "") ||
      DEFAULT_INTERVAL;

    this.timer = new Timer(() => this._tick(), { interval });
    if (this.pauseReasons.size > 0) {
      this.timer.pause();
    }
    if (POLICY.keyboard) {
      this.keyboard.attach();
    }
  }

  _stop() {
    this.timer?.destroy();
    this.timer = null;
    this.keyboard.detach();
  }
}

export const slideshowConductor = new SlideshowConductor();
